import type {
  AccrualPeriod,
  LegacyPlan,
  StakePosition,
  Vault,
  VaultKind,
  WithdrawMode,
} from '../../lib/staking';

/** Per-vault accent, carried over from the INCPT prototype. */
export const VAULT_ACCENT: Record<VaultKind, string> = {
  liquid: '#34c759',
  monthly: '#45a9c4',
  maximum: '#2f7fd8',
};

/** The discontinued line reads amber everywhere, matching its OLD badge. */
export const LEGACY_ACCENT = '#b7791f';

/** Profit is always green, whatever the vault's accent. */
export const PROFIT_GREEN = '#22b45e';

/** `#rrggbb` + alpha → `rgba(...)`, for tints derived from an accent. */
export function tint(hex: string, alpha: number) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** Full USDT precision (6 decimals, trailing zeros trimmed, at least cents) — hourly profit is sub-cent. */
export const fmtUsd = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

/** Shrink a one-line figure so up to 6-decimal amounts stay inside the card. */
export const fitFontSize = (text: string, base: number, maxChars: number) =>
  text.length <= maxChars ? base : Math.max(Math.round(base * 0.55), Math.floor((base * maxChars) / text.length));

/** Preserve all transferable USDT precision without padding insignificant zeroes. */
export const fmtUsdt = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 6 })}`;

export const fmtWhole = fmtUsdt;

export const fmtDate = (iso: string, locale: string) =>
  new Date(iso).toLocaleDateString(locale === 'ru' ? 'ru-RU' : 'en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

export const fmtDateTime = (iso: string, locale: string) =>
  new Date(iso).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** i18n key prefix for a vault, e.g. `stake.liquidName`. */
export const vaultKey = (kind: VaultKind, suffix: string) => `stake.${kind}${suffix}`;

const LEGACY_KEY: Record<string, string> = {
  'legacy-liquid': 'legacyLiquid',
  'legacy-profit': 'legacyProfit',
  'legacy-high': 'legacyHigh',
};

type T = (k: string, o?: Record<string, unknown>) => string;

/** Everything a position card needs to render, from whichever line it runs on. */
export interface PositionMeta {
  accent: string;
  /** Full product name, e.g. "Maximum Vault". */
  name: string;
  /** Compact name for the card header. */
  short: string;
  /** "80–100% APY" for a vault, "7% a month" for a legacy plan. */
  rate: string;
  payout: string;
  principal: string;
  withdrawMode: WithdrawMode;
  accrual: AccrualPeriod;
  /** Daily compounding rate — legacy plans are quoted monthly, so divide. */
  dailyRate: number;
  legacy: boolean;
  topUpAllowed: boolean;
  /** Vault deposit cap in USD; 0 on the legacy line. */
  maxAmount: number;
  /** Term length in days — used for the principal lock-up bar when present. */
  termDays: number;
}

export function positionMeta(
  p: StakePosition,
  vault: Vault | undefined,
  plan: LegacyPlan | undefined,
  t: T,
): PositionMeta {
  if (vault) {
    const kind = vault.kind;
    return {
      accent: VAULT_ACCENT[kind],
      name: t(vaultKey(kind, 'Name')),
      short: t(vaultKey(kind, 'Short')),
      rate: t('stake.apy', { min: vault.apyMin, max: vault.apyMax }),
      payout: t(vaultKey(kind, 'Payout')),
      principal: t(vaultKey(kind, 'Principal')),
      withdrawMode: vault.withdrawMode,
      accrual: vault.accrual,
      dailyRate: vault.dailyRate,
      legacy: false,
      topUpAllowed: vault.topUpAllowed,
      maxAmount: vault.maxAmount,
      termDays: vault.termDays,
    };
  }

  const key = plan ? LEGACY_KEY[plan.id] : undefined;
  return {
    accent: LEGACY_ACCENT,
    name: key ? t(`stake.${key}Name`) : `#${p.id}`,
    short: key ? t(`stake.${key}Short`) : `#${p.id}`,
    rate: t('stake.legacyRate', { rate: ((plan?.monthlyRate ?? 0) * 100).toFixed(0) }),
    payout: plan?.accrual === 'daily' ? t('stake.liquidPayout') : t('stake.monthlyPayout'),
    principal:
      plan?.withdrawMode === 'full' ? t('stake.liquidPrincipal') : t('stake.monthlyPrincipal'),
    withdrawMode: plan?.withdrawMode ?? 'full',
    accrual: plan?.accrual ?? 'daily',
    // A legacy plan quotes a monthly rate; the share card wants it per day.
    dailyRate: (plan?.monthlyRate ?? 0) / 30,
    legacy: true,
    topUpAllowed: false,
    maxAmount: 0,
    termDays: 365,
  };
}
